/* Who moved this Event, and when — STORY-012.
 *
 * The story's "so that" is that **"who confirmed this" is answerable**, and a
 * status column cannot answer it: it holds only the latest answer and
 * overwrites the previous one every time. This is the read side of the trail
 * that makes the question answerable at all, and `security.md` requires the
 * trail whether or not anybody reads it.
 *
 * An Event nobody has moved renders nothing — an empty panel headed "history"
 * is a worse answer than no panel. That is different from a failed load, which
 * says so.
 */

import type { EventStatus } from '@eventuley/contracts';
import type { MessageKey } from '@eventuley/i18n';
import { formatDateTime } from '@eventuley/i18n';
import { NetworkError } from '../../../shared/api/client.js';
import { useI18n } from '../../../shared/i18n/index.js';
import { Callout } from '../../../shared/ui/index.js';
import { useEventHistory } from '../api/index.js';

const LABELS = {
  idea: 'events.status.idea',
  planned: 'events.status.planned',
  confirmed: 'events.status.confirmed',
  completed: 'events.status.completed',
  cancelled: 'events.status.cancelled',
} as const satisfies Record<EventStatus, MessageKey>;

export interface StatusHistoryProps {
  readonly organisationId: string;
  readonly eventId: string;
}

export function StatusHistory({ organisationId, eventId }: StatusHistoryProps) {
  const { t, formatLocale } = useI18n();
  const { data, error } = useEventHistory(organisationId, eventId);

  /* The browser's own zone. The one place in the product where that is the
   * right answer rather than the bug — see the note beside the timestamp. */
  const readerTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  if (error) {
    return (
      <Callout tone="danger">
        {error instanceof NetworkError ? t('error.network') : t('error.unexpected')}
      </Callout>
    );
  }

  const changes = data?.changes ?? [];
  /* Nothing yet is not worth a heading. Most Events are ideas nobody has
   * committed to, and an empty panel on every one of them is noise. */
  if (changes.length === 0) return null;

  return (
    <section className="settings__section">
      <h2>{t('events.lifecycle.history_heading')}</h2>
      <ul className="row-list">
        {changes.map((change) => (
          <li className="row" key={`${change.at}-${change.to}`}>
            <span className="row__main">
              <span className="row__title">
                {t('events.lifecycle.history_line', {
                  from: t(LABELS[change.from]),
                  to: t(LABELS[change.to]),
                })}
              </span>
              <span className="row__meta">
                {t('events.lifecycle.history_by', {
                  /* One fixed anonymous label from the catalogue for an erased
                     account — the words never travel from the server, because
                     `i18n.md` forbids storing a rendered localized string. */
                  who: change.by.anonymised ? t('events.person_removed') : change.by.displayName,
                  /* The READER's zone, not the Event's.
                     
                     `i18n.md` scopes the venue-timezone rule to an Event's own
                     time — the hour it starts, which belongs where it happens.
                     This is a record of when a PERSON acted, which belongs to
                     whoever is reading it. Rendering it in the Venue's zone
                     told a London Owner they had confirmed an Istanbul Event at
                     02:44, for something they did at 23:44 the evening before. */
                  when: formatDateTime(change.at, {
                    locale: formatLocale,
                    timeZone: readerTimezone,
                  }),
                })}
              </span>
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
